// Foundations
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { delay, finalize, skip, switchMap, take } from 'rxjs/operators';

// Services
import { AnalyticsService } from '@app/services/analytics.service';
import { LoadingIndicatorService } from '@app/services/loading-indicator.service';
import { PaymentService } from '@app/services/payment.service';
import { MyProfileService } from '@app/services/my-profile.service';
import { DialogService } from '@app/services/dialog.service';

// Models
import { PaymentCreditCard } from '@app/models/payment/payment-card.model';

// Components
import { ConfirmDialogComponent } from '@app/components/_share/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-payment-list',
  templateUrl: './payment-list.component.html',
  styleUrls: ['./payment-list.component.scss'],
})
export class PaymentListComponent implements OnInit, AfterViewInit, OnDestroy {
  // #region -> Property <-
  // ----------

  isChangeMode = false;

  // If the "preferred location" has enabled CCP (Credit Card Processing).
  isCcpEnabledInPreferredLocation = true;

  primaryPaymentCreditCard: PaymentCreditCard;
  otherPaymentCreditCardList: PaymentCreditCard[] = [];

  currentSelectedPrimaryPaymentCreditCard: PaymentCreditCard;

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Constructor <-
  // ----------

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private _analytics: AnalyticsService,
    private dialogService: DialogService,
    private dialog: MatDialog,
    private loadingIndicatorService: LoadingIndicatorService,
    private myProfileService: MyProfileService,
    private changeDetectorRef: ChangeDetectorRef,
    private paymentService: PaymentService
  ) {}

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Lifecycle <-
  // ----------

  ngOnInit(): void {
    //
    this._fetchData();
  }

  ngAfterViewInit(): void {
    //
    this._prepData();
  }

  ngOnDestroy(): void {}

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - UI <-
  // ----------

  enterChangeMode(): void {
    this._analytics.logLinkTap('paymentList_changePrimaryPayment');

    this.isChangeMode = true;
  }

  onAddCreditCard(): void {
    this._analytics.logLinkTap('paymentList_addNewPayment');

    this.router.navigate(['./add'], { relativeTo: this.route });
  }

  onEditCreditCard(event): void {
    //
    this.router.navigate(['./' + event], { relativeTo: this.route });
  }

  onChangeSelectedPrimary(event): void {
    // Update the "selection"
    this.currentSelectedPrimaryPaymentCreditCard = event as PaymentCreditCard;
  }

  onDeleteCreditCard(event): void {
    //
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'general-dialog',
      data: {
        title: 'Remove This Payment Method',
        content: 'Are you sure you want to delete this payment method?',
        cancelText: 'No',
        okText: 'Delete',
        okFn: () => {
          dialogRef.close();

          this.paymentService.deletePaymentCard(event).subscribe(
            () => {
              // Updates
              this.paymentService.getPaymentCreditCards().subscribe();
            },
            (errorMessage) => {
              // Show error info
              this.dialogService.showAlert({
                title: 'Cannot Delete',
                content: errorMessage,
              });
            }
          );
        },
      },
      autoFocus: false,
    });
  }

  updatePrimaryCreditCard() {
    if (this.currentSelectedPrimaryPaymentCreditCard) {
      //
      this.paymentService
        .setPrimaryPaymentCard(this.currentSelectedPrimaryPaymentCreditCard)
        .pipe(switchMap(() => this.paymentService.getPaymentCreditCards()))
        .subscribe(() => {
          // Updates
          this.isChangeMode = false;
          this.changeDetectorRef.detectChanges();

          //
          const scrollToTop = window.setInterval(() => {
            const pos = window.pageYOffset;
            if (pos > 0) {
              window.scrollTo(0, pos - 20);
            } else {
              window.clearInterval(scrollToTop);
            }
          }, 2);
        });
    }
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Private Method <-
  // ----------

  _fetchData() {
    this.loadingIndicatorService.show();
    this.myProfileService.fetchLocationList();
    this.paymentService.getPaymentCreditCards().subscribe();
  }

  _prepData() {
    //
    combineLatest([
      // Skip the "EMPTY" state
      this.paymentService.paymentCreditCards$.pipe(skip(1)),
      this.myProfileService.preferredLocation$.pipe(skip(1)),
    ])
      .pipe(
        finalize(() => {
          this.loadingIndicatorService.hide();
        })
      )
      .subscribe(([newPaymentCreditCards, preferredLocation]) => {
        const processed = this.paymentService.processPaymentCreditCardList(
          newPaymentCreditCards
        );
        this.primaryPaymentCreditCard = processed.primary;
        this.otherPaymentCreditCardList = processed.others;
        // Also mark the "primary" one as "selected"
        this.currentSelectedPrimaryPaymentCreditCard = processed.primary;

        //
        this.isCcpEnabledInPreferredLocation =
          this.myProfileService.isCcpEnabled(preferredLocation);

        //
        this.loadingIndicatorService.hide();
        this.changeDetectorRef.markForCheck();
      });
  }
  // ----------
  // #endregion

  /////////////////////////////
}
